import { FeatureFlag, FeatureFlags, FeatureSet } from '@/@core/models/FeatureFlags';

export class FeatureClient {
  constructor(
    private featureFlags: Partial<FeatureFlags>,
    private currentFeatureSet: FeatureSet
  ) {}

  public setFlags(flags: Partial<FeatureFlags>): void {
    this.featureFlags = flags;
  }

  public setFeatureSet(featureSet: FeatureSet): void {
    this.currentFeatureSet = featureSet;
  }

  public extensibleSummarizeEnabled(): boolean {
    return this.getFlag('enableExtensibleSummarize');
  }

  public nlsEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.BETA);
  }

  public deepResearchEnabled(): boolean {
    return this.getFlag('enableDeepResearch');
  }

  public chatEnabled(): boolean {
    return this.getFlag('enableChat');
  }

  public localChatHistoryEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.JUNE_00);
  }

  public tokenStreamingEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.JUNE_00);
  }

  public translateEnabled(): boolean {
    return this.getFlag('enableTranslate');
  }

  public extensibleGenerateEnabled(): boolean {
    return this.getFlag('enableExtensibleGenerate');
  }

  public homepageEnabled(): boolean {
    return this.getFlag('enableHomepage');
  }

  public personalAgentsEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.BETA);
  }

  public accessibilityEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.JUNE_00);
  }

  public signLanguageEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.BETA);
  }

  public easyLanguageEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.BETA);
  }

  public accessibilityContactUsEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.JULY_00);
  }

  public customizationViewEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.BETA);
  }

  public julyFlagEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.JULY_00);
  }

  public enableMcpTools(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.BETA);
  }

  public collectionsEnabled(): boolean {
    return this.isFeatureAvailableInFeatureSet(FeatureSet.OCTOBER_00);
  }

  private getFlag(flagKey: FeatureFlag): boolean {
    return this.featureFlags[flagKey] ?? false;
  }

  /**
   * Determines if a feature is available in the current feature set.
   * YYMMNN = YY(year) + MM(month) + NN(serially incremented release number within the month starting from 00).
   *
   * BETA feature set shows all features.
   * For date-based feature sets (YYMMNN format), a feature is available if:
   * - The current feature set's month is later than the feature's release month, or
   * - The current feature set is in the same month but with a higher or equal release number
   */
  private isFeatureAvailableInFeatureSet(featureSet: FeatureSet): boolean {
    const currentFeatureSet = this.currentFeatureSet.toString();

    // Legacy feature set '1' shows no features that require a specific feature set
    if (currentFeatureSet === FeatureSet.LEGACY) {
      return false;
    }

    if (currentFeatureSet === FeatureSet.BETA) {
      return true;
    }

    const currentFeatureSetDate = this.parseFeatureSetDate(currentFeatureSet);
    const targetFeatureSetDate = this.parseFeatureSetDate(featureSet.toString());

    if (!currentFeatureSetDate || !targetFeatureSetDate) {
      return false; // Invalid date format
    }

    return currentFeatureSetDate >= targetFeatureSetDate;
  }

  private parseFeatureSetDate(featureSet: string): Date | null {
    if (featureSet === FeatureSet.BETA) return null;

    if (featureSet.length !== 6) return null;

    const year = parseInt('20' + featureSet.substring(0, 2));
    const month = parseInt(featureSet.substring(2, 4));
    const releaseNumber = parseInt(featureSet.substring(4, 6));

    if (
      isNaN(year) ||
      isNaN(month) ||
      isNaN(releaseNumber) ||
      month < 1 ||
      month > 12 ||
      releaseNumber < 0 ||
      releaseNumber > 99
    ) {
      return null;
    }

    const baseDate = new Date(year, month - 1, 1);
    baseDate.setMilliseconds(releaseNumber);
    return baseDate;
  }
}

export const FEATURE_CLIENT = new FeatureClient({}, FeatureSet.LEGACY);
